/* eslint-disable no-console */
import { create, StoreApi, UseBoundStore } from 'zustand';
import { toast } from 'react-toastify';
import getErrorMessage from '../utils/error';
import { IInvoice, IPaginationData, IParamsSearch, IResponse } from '../types/types';
import { createInvoice, deleteInvoice, getAllInvoice, getInvoiceById, updateInvoice } from '../utils/api';

interface IUseInvoice {
  loading: boolean;
  invoice: IInvoice | null;
  invoices: IInvoice[] | null;
  paginationData: IPaginationData | null;
  abortController: AbortController | null;
  cancelFetchInvoice: () => void;
  fetchInvoice: (params?: IParamsSearch) => Promise<IResponse>;
  getInvoice: (id: string) => Promise<IResponse>;
  deleteInvoice: (id: string) => Promise<IResponse>;
  createInvoice: (data: IInvoice) => Promise<IResponse>;
  updateInvoice: (id: string, data: IInvoice) => Promise<IResponse>;
  resetInvoice: () => void;
  resetInvoices: () => void;
}

const useInvoice: UseBoundStore<StoreApi<IUseInvoice>> = create((set, get) => ({
  loading: false,
  invoice: null,
  invoices: null,
  paginationData: null,
  abortController: null,
  cancelFetchInvoice: () => {
    const { abortController } = get();
    if (abortController && !abortController.signal.aborted) {
      abortController.abort();
      set({ loading: false, abortController: null });
    }
  },
  fetchInvoice: async (params = {}) => {
    const newAbortController = new AbortController();

    try {
      set(() => ({ loading: true }));

      if (get().abortController) {
        get().abortController?.abort();
      }

      set(() => ({ abortController: newAbortController }));

      // Construct query parameters
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());

      // Add filters
      if (params.filters) {
        Object.entries(params.filters).forEach(([key, value]) => {
          if (value) queryParams.append(`${key}`, value.toString());
        });
      }

      // Add sorting
      if (params.sorting) {
        queryParams.append('sortBy', params.sorting.field);
        queryParams.append('sortOrder', params.sorting.order);
      }

      const { success, message, data } = await getAllInvoice(queryParams.toString(), newAbortController.signal);

      // Only update state if this request wasn't aborted
      if (!newAbortController.signal.aborted) {
        if (!success) {
          throw new Error(message);
        }

        set(() => ({
          invoices: data.data,
          paginationData: data.pagination,
        }));
      }

      return {
        success: true,
        message,
      };
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.log('Request dibatalkan');
        return {
          success: false,
          message: 'Request dibatalkan',
        };
      }

      const errorMessage = getErrorMessage(error);
      console.error(errorMessage);
      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      const currentController = get().abortController;
      if (currentController === newAbortController && !currentController?.signal.aborted) {
        set(() => ({ loading: false }));
      }
    }
  },
  getInvoice: async (id) => {
    try {
      set(() => ({ loading: true }));
      const { success, message, data } = await getInvoiceById(id);

      if (!success) {
        throw new Error(message);
      }

      set(() => ({ invoice: data }));

      return {
        success: true,
        message,
      };
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      console.error(errorMessage);
      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      set(() => ({ loading: false }));
    }
  },
  deleteInvoice: async (id) => {
    const createToast = toast.loading('Delete invoice...');

    try {
      set(() => ({ loading: true }));

      const prevInvoices = get().invoices;

      const { success, message } = await deleteInvoice(id);

      if (!success) {
        throw new Error(message);
      }

      set({ invoices: prevInvoices?.filter((invoice) => invoice.invoice_id !== id) });

      toast.update(createToast, {
        position: 'top-right',
        render: 'Delete invoice successfully',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });

      return {
        success: true,
        message,
      };
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      console.error(errorMessage);

      // Rollback jika error terjadi
      set({ invoices: get().invoices });

      toast.update(createToast, {
        position: 'top-right',
        render: errorMessage,
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });

      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      set(() => ({ loading: false }));
    }
  },
  createInvoice: async (data) => {
    const createToast = toast.loading('Create invoice...');
    try {
      set(() => ({ loading: true }));
      const { success, message } = await createInvoice(data);

      if (!success) {
        throw new Error(message);
      }

      toast.update(createToast, {
        position: 'top-right',
        render: 'Create invoice successfully',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });

      return {
        success: true,
        message,
      };
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      console.error(errorMessage);

      toast.update(createToast, {
        position: 'top-right',
        render: errorMessage,
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });

      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      set(() => ({ loading: false }));
    }
  },
  updateInvoice: async (id, data) => {
    const createToast = toast.loading('Update invoice...');

    try {
      set(() => ({ loading: true }));
      const { success, message } = await updateInvoice(id, data);

      if (!success) {
        throw new Error(message);
      }

      toast.update(createToast, {
        position: 'top-right',
        render: 'Update invoice successfully',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });

      return {
        success: true,
        message,
      };
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      console.error(errorMessage);

      toast.update(createToast, {
        position: 'top-right',
        render: errorMessage,
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });

      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      set(() => ({ loading: false }));
    }
  },
  resetInvoice: () => set({ invoice: null }),
  resetInvoices: () => set({ invoices: null, paginationData: null }),
}));

// Subscribe untuk mengecek perubahan state
// eslint-disable-next-line @typescript-eslint/no-unused-vars
// const unsubscribe = useInvoice.subscribe((state) => {
//   console.log('State invoice:', state);
// });

export default useInvoice;
